import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AddGroupIcon from '@mui/icons-material/PlaylistAdd';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { createContext, FC, FormEventHandler, forwardRef, MouseEventHandler, MutableRefObject, RefAttributes, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, AutocompleteRenderGetTagProps, AutocompleteRenderOptionState, Box, Button, Checkbox, Chip, Divider, FilterOptionsState, FormHelperText, IconButton, Link, MenuItem, Paper, Popper, Select, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useRecordContext } from "ra-core";
import { ClassificationRuleDto } from "@/gen/api/securefile";
import { useLmGlobalDisabledMsg } from "@/contexts/LocalMessageHookProtocols/useLmGlobalDisabledMsg";
import { useLmStatesPropagate } from "@/contexts/LocalMessageHookProtocols/useLmStatesPropagate";
import { LocalMessageLocation, LocalMessageSubPub, useLocalMessagingContext, FilterHelper as Flt } from "@/contexts/LocalMessagingRoot";
import { promiseWithResolvers } from "@/utils/promiseWithResolvers";
import { groupRules, operatorRules, opOptions, PickerOptionAmbientDataContextProps, pickerRules, RuleDisplayKind, RuleDisplayTree, useRuleDisplayPresetContext } from "./RuleDisplayPrimitives";
import { SimpleOption2 } from "../governance/search/AmbientFacilitiesContext";


export const RuleDisplayRoot: FC<{globalDisabled:boolean}> = ({globalDisabled}) => {
  const ruleComposeCtrls = useRuleComposeController(globalDisabled)
  const { states, switchRootKind, addChildToVirtualRoot, addChildGroupToVirtualRoot, startSelectChildRuleToWrap, registerRef, getComposedRule } = ruleComposeCtrls
  
  const noEffectRuleDefined = states.validationTouched && !getComposedRule()
  const { rightPickerOptions: {useDataSource} } = useRuleDisplayPresetContext()
  
  const showVirtualRoot = () => (
    <>
      <Box className="group-actions-placement" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
        {!states.isVirtualRootOperator &&
          <Select value={states.virtualRootGroup.Kind} size="small" sx={{ mr: 2 }}
            disabled={globalDisabled}
            onChange={(e) => switchRootKind(e.target.value as any)}
          >
            <MenuItem value="GroupAnd">Group And</MenuItem>
            <MenuItem value="GroupOr" >Group Or</MenuItem>
          </Select>}  
        <Tooltip title="Add Clause">
          <IconButton color="primary" disabled={globalDisabled} onClick={addChildToVirtualRoot}>
            <AddIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Add Subgroup">
          <IconButton color="primary" disabled={globalDisabled} onClick={addChildGroupToVirtualRoot}>
            <AddGroupIcon /></IconButton>
        </Tooltip>
        <Tooltip title="Wrap Clause in Subgroup">
          <IconButton color="primary" disabled={globalDisabled} onClick={startSelectChildRuleToWrap}>
            <UnfoldMoreIcon /></IconButton>
        </Tooltip>
        {noEffectRuleDefined &&
          <FormHelperText error sx={{ ml: 20 }}>There is no effective rule defined</FormHelperText>}
      </Box>
      <LocalMessageLocation>
        <GroupRule id="topGroup" rule={states.virtualRootGroup} hideBtns={true} getCtrlsRef={registerRef} />
      </LocalMessageLocation>
    </>)

  return (
    <Box
      sx={{
        '& .layout': { display: 'grid', gridTemplateColumns: '1fr 48px', },
        '& .layout.extended': { display: 'grid', gridTemplateColumns: '42px 1fr 48px', },
        '& .close-btn-placement': { gridRow: '1', },
        '& .group-actions-placement': { gridRow: '1', },
        '& .group-children-placement': { gridColumn: '1/3', gridRow: '2',
          '& div.alt-line': {
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            bgcolor: 'background.paper',
          }, 
        },
        '& .operator-placement': { gridRow: '1', },
      }}
    >
      <RuleComposeContextProvider values={ruleComposeCtrls}>
        <PickerOptionAmbientDataContext.Provider value={useDataSource()}>
        {!states.usingVirtualRoot && !globalDisabled &&
          <LocalMessageLocation>
            <GroupRule id="topGroup" rule={states.rule} getCtrlsRef={registerRef} allowSwitchKind={true} displayNoRuleDefinedWarning={noEffectRuleDefined} />
          </LocalMessageLocation>}
        {!states.usingVirtualRoot && globalDisabled &&
          <Box><LocalMessageLocation>
            <GroupRule id="topGroup" rule={states.rule} getCtrlsRef={registerRef} allowSwitchKind={true} displayNoRuleDefinedWarning={noEffectRuleDefined} />
          </LocalMessageLocation></Box>}
        {states.usingVirtualRoot && states.virtualRootGroup.Kind === 'GroupAnd' && showVirtualRoot()}
        {states.usingVirtualRoot && states.virtualRootGroup.Kind === 'GroupOr' && 
          <Box>
            {showVirtualRoot()}
          </Box>}
        </PickerOptionAmbientDataContext.Provider>
      </RuleComposeContextProvider>
    </Box>
  )
}

export interface RuleComposeContextProps {
  states: {
    createMode: boolean;
    rule: RuleDisplayTree;
    usingVirtualRoot: boolean;
    isVirtualRootOperator: boolean;
    virtualRootGroup: RuleDisplayTree;
    original: RuleDisplayTree;
    validationTouched: boolean;
    isDirty: boolean;
  };
  trackRuleTreeDirty: (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => void;
  getComposedRule: () => RuleDisplayTree|null;
  registerRef: (cb: () => GroupRuleContextProps) => void;
  switchRootKind: (k: "GroupAnd" | "GroupOr") => void;
  addChildToVirtualRoot: () => void;
  addChildGroupToVirtualRoot: () => void;
  startSelectChildRuleToWrap: () => void;
}

const RuleComposeContext = createContext<RuleComposeContextProps>({} as any)

export const RuleComposeContextProvider : FC<{values:RuleComposeContextProps}> = ({values, children}) => {
  return (
    <RuleComposeContext.Provider value={values}>
      <NestedGroupContext.Provider value={
        useMemo(() => ({ level: 0, updateChildRule: values.trackRuleTreeDirty }), 
                [values.trackRuleTreeDirty])
      }>
        {children}
      </NestedGroupContext.Provider>
    </RuleComposeContext.Provider>
  )
}


const ruleComposeStatesInit = (rule: RuleDisplayTree, initialRule: ()=>RuleDisplayTree) => {
  const createMode = !(rule.Kind)
  const usingVirtualRoot = createMode || !groupRules.has(rule?.Kind as any)
  const isVirtualRootOperator = usingVirtualRoot
  const virtualRootGroup: RuleDisplayTree = { 
    Kind: 'GroupAnd', 
    Group: [!createMode ? (rule) : initialRule()] 
  }    
  return ({
    createMode,
    rule,
    usingVirtualRoot,
    isVirtualRootOperator,
    virtualRootGroup,
    original: virtualRootGroup,
    validationTouched: false,
    isDirty: false,
  })
}

const  useRuleComposeController = (globalDisabled?: boolean): RuleComposeContextProps => {
  const record = useRecordContext()
  const dto: ClassificationRuleDto|undefined = record as any
  const { defaults: {rule: initialRule} } = useRuleDisplayPresetContext()

  const rule = useMemo(() => {
    return JSON.parse(dto?.ruleDisplayTree ?? '{}') as RuleDisplayTree
  }, [dto?.ruleDisplayTree])

  const [states, setStates] = useState(() => ruleComposeStatesInit(rule, initialRule))
  useEffect(() => {
    setStates(ruleComposeStatesInit(rule, initialRule))
  }, [rule, initialRule])

  const getCtrlsRef = useRef<(()=>GroupRuleContextProps)>(() => (null as any))

  const getComposedRule = useCallback(() => {
    const root = getCtrlsRef.current().states.composedRule
    let composedRule = root
    if (states.usingVirtualRoot && states.isVirtualRootOperator) {
      composedRule = root.Group![0]
    }
    const simplify = (t: RuleDisplayTree): RuleDisplayTree|null => {
      if (t == null) { return null }
      if (!groupRules.has(t.Kind)) { return t }
      if (!t.Group) { return null }
      if (t.Group.length === 0) { return null }
      else if (t.Group.length === 1) {
        return simplify(t.Group[0])
      } else {
        const group1: RuleDisplayTree[] = t.Group.map(simplify).filter(x => x) as any
        return (
          group1.length === 0 ? null
        : group1.length === 1 ? group1[0]
        : {...t, Group: group1 })
      }
    }
    return composedRule ? simplify(composedRule) : null
  }, [states.isVirtualRootOperator, states.usingVirtualRoot])

  const {informDirty} = useLmStatesPropagate({
    key: 'composedRule',
    asLowerStage: {
      collectStates: {
        handler(assembleSubStates) {
          return async () => {
            setStates(v => ({...v, validationTouched: true}))
            await assembleSubStates(['topGroup'])
            // run pass the above line means validation passed
            const r = getComposedRule()
            if (!r) { throw 'no rule def' }
            return r
          }
        },
        deps: [getComposedRule]
      }
    }
  })

  useLmRuleDisplayDeltaMsg({
    key: 'composedRule',
    asLowerStage: {
      receiveDelta: {
        handler(sendDownDelta) {
          return (cmd) => {
            const d = cmd.delta
            setStates(v => {
              const d_is_group = groupRules.has(d.Kind)
              const isVirtualRootOperator = !d_is_group
              const virtualRootGroup : RuleDisplayTree = d_is_group ? d : { Kind: 'GroupAnd', Group: [d] }
              setTimeout(() => {
                sendDownDelta('topGroup', virtualRootGroup)
                informDirty(true)
              })
              return {...v, isDirty: true, validationTouched: false, isVirtualRootOperator }
            })
          }
        },
        deps: []
      }
    }
  })

  const methods = useMemo(() => {
    const trackRuleTreeDirty = (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => {
      setStates(v => {
        if (!v.usingVirtualRoot) {
          v.isDirty = prevRule !== updRule
        } else if (v.isVirtualRootOperator) {
          v.isDirty = prevRule !== updRule
        } else {
          // v.isVirtualRootOperator === false
          v.isDirty = true
        }
        informDirty(v.isDirty)
        return {...v}
      })
    }
    const registerRef = (cb:() => GroupRuleContextProps) => { getCtrlsRef.current = cb };
    const switchRootKind = (k: 'GroupAnd'|'GroupOr') => {
      setStates(v => {
        if (v.virtualRootGroup.Kind === k) { return v }
        const tree = getCtrlsRef.current().states.composedRule
        const flipKind = (t:RuleDisplayTree) => {
          if (t == null) { return }
          if (t.Kind === 'GroupAnd') {
            t.Kind = 'GroupOr'
          } else if (t.Kind === 'GroupOr') {
            t.Kind = 'GroupAnd'
          } else { return }
          for (const s of t.Group ?? []) {
            flipKind(s)
          }
        }
        flipKind(tree)
        return {...v, virtualRootGroup: {...tree}}
      })
    }  
    const addChildToVirtualRoot = () => {
      getCtrlsRef.current().addChildRule("Equals")
      setStates(v => {
        if (!v.isVirtualRootOperator) { return v }
        return {...v, isVirtualRootOperator: false }
      })
    }
    const addChildGroupToVirtualRoot = () => {
      setStates(v => {
        setTimeout(() => {
          getCtrlsRef.current().addChildRule(v.virtualRootGroup.Kind === 'GroupAnd' ? "GroupOr" : "GroupAnd")
        })
        if (!v.isVirtualRootOperator) { return v }
        return {...v, isVirtualRootOperator: false }
      })
    }
    const startSelectChildRuleToWrap = () => {
      getCtrlsRef.current().startSelectChildRuleToWrap()
    }
    return {
      trackRuleTreeDirty,
      registerRef,
      switchRootKind,
      addChildToVirtualRoot,
      addChildGroupToVirtualRoot,
      startSelectChildRuleToWrap,
    }
  }, [informDirty])
  useEffect(() => {
    if (dto) { // dto changed, reset residual dirty state
      informDirty(false)
    }
  }, [dto, informDirty])

  return useMemo(() => ({
    states, 
    globalDisabled: !!globalDisabled,
    getComposedRule,
    ...methods
  }), [states, globalDisabled, getComposedRule, methods])
}


const RuleDisplay: FC<{id:string, rule:RuleDisplayTree}> = ({id: key, rule}) => {
  if (pickerRules.has(rule.Kind)) { return null; }

  return (
    <LocalMessageLocation id={key}>
      {groupRules.has(rule.Kind)    
        ? <GroupRule id={key} rule={rule} />
        : <OperatorRule id={key} rule={rule} />}
    </LocalMessageLocation>
  )
}


const GroupRule: FC<{
  id:string,
  rule:RuleDisplayTree, 
  hideBtns?:boolean,
  allowSwitchKind?:boolean,
  getCtrlsRef?: (x:()=>GroupRuleContextProps)=>void,
  displayNoRuleDefinedWarning?: boolean,
}> = ({id: key, rule, hideBtns, allowSwitchKind, getCtrlsRef, displayNoRuleDefinedWarning}) => {
  const controls = useGroupRuleController(key, rule, allowSwitchKind)
  const { 
    states: { prevGroup, boxedUnboxed, removedRules, added, showWrapChild, composedRule }, 
    nextLevel, color,
    updateChildRule,
    removeChildRule,
    restoreChildRule,
    addChildRule,
    wrapChildRuleInGroup,
    startSelectChildRuleToWrap,
    switchKindFromThisGroup,
    globalDisabled,
  } = controls
  const ctrlRef = useRef(controls)
  ctrlRef.current = controls
  if (getCtrlsRef) {
    getCtrlsRef(() => ctrlRef.current)
  }

  const { leftPickeValueOptions: pvOptions } = useRuleDisplayPresetContext()
  const childLine = (k:string, r: RuleDisplayTree, isAltLine: boolean, isGroup: boolean) => (
    <Box sx={{ width: '100%', my: 0.5, py: 0.5, }} className={`layout${isAltLine ? ' alt-line' : ''}${showWrapChild && !isGroup ? ' extended' : ''}`} >
      {showWrapChild && !isGroup &&
        <Box sx={{ gridColumn: 1, gridRow: 1, display: 'flex', '& > span.MuiCheckbox-root': { my: 'auto' } }}>
          <Tooltip title="Wrap this clause">
            <Checkbox inputProps={{ 'aria-label': 'wrap child rule in group'}} onChange={(e, checked) => checked && wrapChildRuleInGroup(r)} />
          </Tooltip>
        </Box>
      }
      <RuleDisplay id={k} rule={r} />
      <Tooltip title={isGroup ? "remove subgroup" : "remove"}>
        <IconButton aria-label={isGroup ? "remove subgroup" : "remove"} sx={{ mr: 1, }} className="close-btn-placement"
          onClick={() => removeChildRule(r)} disabled={globalDisabled}
        >
          <CloseIcon htmlColor={(isGroup ? colors[nextLevel % colors.length] : undefined)}  />
        </IconButton>
      </Tooltip>
    </Box>);

  return (
    <>
      {!hideBtns &&
        <Box className="group-actions-placement" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
          {!allowSwitchKind
            ? <Typography variant="subtitle2" sx={{ mr: 4, color }}>
                {rule.Kind === "GroupAnd" ? "Group And" : "Group Or"}
              </Typography>
            : <Select value={composedRule.Kind} size="small" sx={{ mr: 2 }}
                disabled={globalDisabled}
                onChange={(e) => switchKindFromThisGroup()}
              >
                <MenuItem value="GroupAnd">Group And</MenuItem>
                <MenuItem value="GroupOr" >Group Or</MenuItem>
              </Select>}
          <Tooltip title="Add Clause">
            <IconButton onClick={() => addChildRule("Equals")} disabled={globalDisabled} color="primary">
              <AddIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Add Subgroup">
            <IconButton onClick={() => addChildRule(rule.Kind === "GroupAnd" ? "GroupOr" : "GroupAnd")} disabled={globalDisabled} color="primary">
              <AddGroupIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Wrap Clause in Subgroup">
            <IconButton onClick={startSelectChildRuleToWrap} color="primary" disabled={globalDisabled} >
              <UnfoldMoreIcon /></IconButton>
          </Tooltip>
          {displayNoRuleDefinedWarning &&
            <FormHelperText error sx={{ ml: 20 }}>There is no effective rule defined</FormHelperText>}
        </Box>}
      <Box className="group-children-placement" sx={{ borderLeft: `2px solid ${color}`, pl: 1.5, bgcolor: 'background.default', my: 0.5, py: 0.5 }}>
        <NestedGroupContext.Provider value={{updateChildRule, level: nextLevel}}>
          {prevGroup.map((r, i) => {
            if (!r) { return null }
            const wrapped = boxedUnboxed[i]
            const r1 = wrapped ?? r
            const isRemoved = removedRules.indexOf(r) > -1
            const isAltLine = i % 2 === 1 && !r1.Group
            const isGroup = !!r1.Group
            const isRuleFliped = r.Kind === 'LogicNot'
            const rActucal = isRuleFliped ? r.Left! : r
            return (
              <Box key={`p${i}`} >
                {!isRemoved 
                  ? childLine(`p${i}`, r1, isAltLine, isGroup)
                  : <Stack direction="row" justifyContent="space-between" alignItems="center" my={1} mr={1}>
                      {isGroup  
                        ? <Typography variant="body2" sx={{ textDecoration: 'line-through' }} color={colors[nextLevel % colors.length]} >
                            {r.Kind === "GroupAnd" ? "Group And" : "Group Or"}
                          </Typography>
                        : <Typography variant="body2" sx={{ textDecoration: 'line-through' }}>
                            <Typography component="span" color="success.light">{pvOptions.find(x => x.key === rActucal.Left!.Value)!.text} </Typography>
                            <Typography component="span" color="info.light" >{(opOptions.find(x => x.key === rActucal.Kind) as any)[isRuleFliped ? 'flipText' : 'text']}</Typography> …
                          </Typography>}
                      <Button onClick={() => restoreChildRule(r)} disabled={globalDisabled} >Restore</Button>
                    </Stack>
                  }
              </Box>
            )
          })}
          {added.stubs.map((r, i) => {
            const isAltLine = (prevGroup.length + i) % 2 === 1 && !r.Group
            const isGroup = !!r.Group
            return (
              <Box key={`a${(r as any).n}`}>
                {childLine(`a${(r as any).n}`, r, isAltLine, isGroup)}
              </Box>
            )
          })}
        </NestedGroupContext.Provider>
      </Box>
    </>
  )
}

// ?? composing rule ??

interface GroupRuleContextProps {
  addChildRule: (kind: RuleDisplayKind) => void;
  removeChildRule: (r: RuleDisplayTree) => void;
  restoreChildRule: (r: RuleDisplayTree) => void;
  updateChildRule: (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => void;
  wrapChildRuleInGroup: (prevRule: RuleDisplayTree) => void;
  startSelectChildRuleToWrap: () => void;
  switchKindFromThisGroup: () => void;
  states: {
    prevGroup: RuleDisplayTree[];
    boxedUnboxed: RuleDisplayTree[];
    removedRules: RuleDisplayTree[];
    updatedRules: RuleDisplayTree[];
    added: {
      stubs: RuleDisplayTree[];
      rules: RuleDisplayTree[];
    };
    composedRule: RuleDisplayTree;
    showWrapChild: boolean;
  },
  nextLevel: number;
  color: string;
  globalDisabled: boolean;
}

interface States {
  prevGroup: RuleDisplayTree[];
  boxedUnboxed: RuleDisplayTree[];
  removedRules: RuleDisplayTree[];
  updatedRules: RuleDisplayTree[];
  added: {
    stubs: RuleDisplayTree[];
    rules: RuleDisplayTree[];
  };
  composedRule: RuleDisplayTree;
}

const groupRuleStatesInit = (rule:RuleDisplayTree) => ({
  prevGroup: rule.Group ?? [],
  boxedUnboxed: [] as RuleDisplayTree[], // it's a sparse array
  removedRules: [] as RuleDisplayTree[], // this field is expected to store elems from prevGroup
  updatedRules: [] as RuleDisplayTree[], // this field is expected to store the children's composedRules; it's a sparse array
  added: {
    n: 0,
    stubs: [] as RuleDisplayTree[],
    rules: [] as RuleDisplayTree[],
  },
  composedRule: { Kind: rule.Kind, Group: [...rule.Group!] } as RuleDisplayTree, // this field's obj ref should remain stable
  globalDisabled: false,
  showWrapChild: false,
})

const useGroupRuleController = (key:string, rule:RuleDisplayTree, allowSwitchKind?:boolean): GroupRuleContextProps => {
  const [states, setStates] = useState(() => groupRuleStatesInit(rule))
  useEffect(() => setStates(groupRuleStatesInit(rule)), [rule])
  const { level, updateChildRule: propagateUp } = useContext(NestedGroupContext)
  const { states:{createMode}} = useContext(RuleComposeContext)

  useLmGlobalDisabledMsg({
    listener: (disabled) => {
      setStates(v => {
        if (v.globalDisabled !== disabled) {
          return {...v, globalDisabled: disabled}
        }
        return v
      })
    }
  })

  const valueEqualsPrev = useCallback((v: typeof states) => {
    if (!allowSwitchKind) {
      return v.updatedRules.every(x => !x) && v.removedRules.every(x => !x) && v.added.rules.every(x => !x)
    } else {
      return rule.Kind === v.composedRule.Kind && v.updatedRules.every(x => !x) && v.removedRules.every(x => !x) && v.added.rules.every(x => !x)
    }
  }, [allowSwitchKind, rule.Kind])

  useLmStatesPropagate({
    key,
    asLowerStage: {
      collectStates: {
        handler(validateComposedRule) {
          return () => {
            const {promise, resolve, reject} = promiseWithResolvers()
            setStates(v => {
              const subStateKeys = [
                ...v.prevGroup.flatMap((r, i) => {
                  return v.removedRules.indexOf(r) > -1 ? [] : [`p${i}`]
                }),
                ...v.added.stubs.map((r, i) => `a${(r as any).n}`)
              ]
              if (subStateKeys.length > 0) {
                validateComposedRule(subStateKeys).then(resolve, reject) 
              } else {
                resolve(true)
              }
              return v
            })
            return promise
          }
        },
        deps: []
      }
    }
  })
  const sendDownDeltaRef = useRef((subkey: string, subDelta: RuleDisplayTree) => {})

  const modifiers = useMemo(() => {
    const recompose = (v: States) => {
      const arr = [...v.prevGroup];
      v.updatedRules.forEach((u, j) => arr[j] = u);
      arr.push(...v.added.rules.filter(x => Boolean(x)));
      v.removedRules.flatMap((rm) => {
        const k = v.prevGroup.indexOf(rm);
        return k > -1 ? [k] : []
      }).sort((a, b) => b - a).forEach(i => { arr.splice(i, 1) });
      v.composedRule.Group = arr;
    }

    const addChildRule = (kind: RuleDisplayKind) => {
      setStates(v => {
        if (pickerRules.has(kind)) { return v; }
        v.added.stubs.push({ 
          Kind: kind, 
          Group: groupRules.has(kind) ? [] : undefined,
          ...{n: v.added.n},
        })
        v.added.rules.push(null as any)
        v.added.n += 1
        // recompose(v);
        return {...v}
      })
    }
    
    let fstRuleDelta = null as RuleDisplayTree|null

    const removeChildRule = (r: RuleDisplayTree) => {
      setStates(v => {
        if (v.added.stubs.indexOf(r) > -1) {
          const i = v.added.stubs.indexOf(r)
          v.added.stubs.splice(i, 1)
          v.added.rules.splice(i, 1)
        } else if (v.removedRules.indexOf(r) > -1) {
          return v
        } else {
          let i = v.boxedUnboxed.indexOf(r)
          v.removedRules.push(i > -1 ? v.prevGroup[i] : r)
          i = i < 0 ? v.prevGroup.indexOf(r) : i
          if (createMode && i === 0) {
            fstRuleDelta = v.updatedRules[0]
          }
        }
        recompose(v);
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        return {...v}
      })
    }
    
    const restoreChildRule = (r: RuleDisplayTree) => {
      setStates(v => {
        const i = v.removedRules.indexOf(r)
        if (i < 0) { return v }
        v.removedRules.splice(i, 1)
        if (createMode && i === 0 && fstRuleDelta) {
          v.updatedRules[0] = fstRuleDelta
          setTimeout(() => {
            sendDownDeltaRef.current('p0', fstRuleDelta!)
            fstRuleDelta = null
          }, 10)
        }
        recompose(v);
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        return {...v}
      })
    }
    
    const updateChildRule = (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => {
      setStates(v => {
        let i = v.boxedUnboxed.indexOf(prevRule)
        i = i < 0 ? v.prevGroup.indexOf(prevRule) : i
        if (i > -1) {
          if (prevRule === updRule) {
            delete v.updatedRules[i];
          } else {
            v.updatedRules[i] = updRule
          }
        } else {
          i = v.added.stubs.indexOf(prevRule)
          if (i > -1) {
            v.added.rules[i] = updRule
          }
        }
        if (i < 0) { return v }
        recompose(v);
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        // return {...v}
        return v
      })
    }

    const wrapChildRuleInGroup = (prevRule: RuleDisplayTree) => {
      setStates(v => {
        v.showWrapChild = false
        const kind = rule.Kind === 'GroupAnd' ? 'GroupOr' : 'GroupAnd'
        let i = v.prevGroup.indexOf(prevRule)
        if (i > -1) {
          const updRule = v.updatedRules[i] ?? {...v.prevGroup[i]}
          if (groupRules.has(updRule.Kind)) { return {...v} }
          v.boxedUnboxed[i] = v.updatedRules[i] = { Kind: kind, Group: [updRule]}
        } else {
          i = v.added.stubs.indexOf(prevRule)
          if (i > -1) {
            const updRule = v.added.rules[i]
            if (updRule && groupRules.has(updRule.Kind)) { return {...v} }
            const a2 : RuleDisplayTree[] = []
            for (const k in v.added.rules) { a2[k] = v.added.rules[k] }
            a2[i] = { Kind: kind, Group: [updRule]}
            v.added.rules = a2
            v.added.stubs[i] = { Kind: kind, Group: [v.added.stubs[i]] }
          }
        }
        if (i < 0) { return {...v} }
        recompose(v)
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v) ? rule : v.composedRule))
        return {...v}
      })
    }

    const startSelectChildRuleToWrap = () => {
      setStates(v => ({...v, showWrapChild: !v.showWrapChild}))
    }

    const switchKindFromThisGroup = () => {
      const flipKind = (t:RuleDisplayTree) => {
        if (t == null) { return }
        if (t.Kind === 'GroupAnd') {
          t.Kind = 'GroupOr'
        } else if (t.Kind === 'GroupOr') {
          t.Kind = 'GroupAnd'
        } else { return }
        for (const s of t.Group ?? []) {
          flipKind(s)
        }
      }
      setStates(v => {
        const v1 = groupRuleStatesInit(rule)
        if (v.composedRule.Kind === rule.Kind) {
          const groups : RuleDisplayTree[] = JSON.parse(JSON.stringify(rule.Group))
          groups.forEach(g => flipKind(g))
          v1.composedRule.Kind = rule.Kind === 'GroupAnd' ? 'GroupOr' : 'GroupAnd'
          v1.prevGroup = groups
          v1.composedRule.Group = groups
        }
        setTimeout(() => 
          propagateUp(rule, 
            valueEqualsPrev(v1) ? rule : v1.composedRule))
        return v1
      })
    }
    return { addChildRule, removeChildRule, restoreChildRule, updateChildRule, wrapChildRuleInGroup, startSelectChildRuleToWrap, switchKindFromThisGroup }
  }, [setStates, rule, valueEqualsPrev, propagateUp])
  
  sendDownDeltaRef.current = useLmRuleDisplayDeltaMsg({
    key,
    asLowerStage: {
      receiveDelta: {
        handler(sendDownDelta) {
          return (cmd) => {
            const s0 = groupRuleStatesInit(rule)
            const d = cmd.delta;
            if (!d.Group) { return }
            const {ms} = d.Group.reduce((acc, d_r, di) => {
              for (let i = Math.max(acc.i, di); i < s0.prevGroup.length; i++) {
                if (ruleEquals(s0.prevGroup[i], d_r)) {
                  if (acc.combo) {
                    const x = acc.ms[acc.ms.length - 1]
                    x.len += 1
                  } else {
                    acc.ms.push({si: i, di, len: 1})
                  }
                  acc.i = i + 1
                  acc.combo = true
                  break;
                } else {
                  acc.combo = false
                }
              }
              return acc
            }, { i: 0, ms: [] as {si:number, di:number, len:number}[], combo: false });
            const mismatches = [{si:-1, di:-1, len:1}, ...ms, {si:s0.prevGroup.length, di:d.Group.length, len:1}].reduce((acc, m_a, i, arr) => {
              const m_b = arr[i + 1]
              if (m_b) {
                const si = m_a.si + m_a.len 
                const di = m_a.di + m_a.len 
                acc.push({ si, di, slen: m_b.si - si, dlen: m_b.di - di })
              }
              return acc
            }, [] as {si:number, di:number, slen:number, dlen:number}[])
            for (const mismatch of mismatches) {
              const lenDiff = mismatch.slen - mismatch.dlen
              if (lenDiff >= 0) {
                for (let i = 0; i < mismatch.dlen; i++) {
                  const r_s = s0.prevGroup[mismatch.si + i]
                  const r_d = d.Group[mismatch.di + i]
                  s0.updatedRules[mismatch.si + i] = r_d
                  if (groupRules.has(r_s.Kind) !== groupRules.has(r_d.Kind)) {
                    s0.boxedUnboxed[mismatch.si + i] = r_d
                  }
                }
                if (lenDiff > 0) {
                  for (let i = 0; i < lenDiff; i++) {
                    s0.removedRules.push(s0.prevGroup[mismatch.si + mismatch.dlen + i])
                  }
                }
              } else {
                for (let i = 0; i < mismatch.slen; i++) {
                  const r_s = s0.prevGroup[mismatch.si + i]
                  const r_d = d.Group[mismatch.di + i]
                  s0.updatedRules[mismatch.si + i] = r_d
                  if (groupRules.has(r_s.Kind) !== groupRules.has(r_d.Kind)) {
                    s0.boxedUnboxed[mismatch.si + i] = r_d
                  }
                }
                const absDiff = Math.abs(lenDiff)
                for (let i = 0; i < absDiff; i++) {
                  const currDR = d.Group[mismatch.di + mismatch.slen + i]
                  s0.added.stubs.push({ Kind: currDR.Kind, Group: groupRules.has(currDR.Kind) ? [] : undefined, ...{n: i}})
                  s0.added.rules.push(currDR)
                }
                s0.added.n = absDiff
              }
            }
            s0.composedRule = JSON.parse(JSON.stringify(d))
            setStates(s0)
            const updatedRules = s0.updatedRules
            const addedRules = s0.added.rules
            setTimeout(() => {
              for (const i in updatedRules) {
                sendDownDelta(`p${i}`, updatedRules[i])
              }
              for (const i in addedRules) {
                sendDownDelta(`a${i}`, addedRules[i])
              }
            }, 20)
          }
        },
        deps: [rule]
      }
    }
  }).sendDownDelta
  
  return { states, ...modifiers, nextLevel: level + 1, color: colors[level % colors.length], globalDisabled: states.globalDisabled }
}

const colors: string[] = [
  '#42a5f5',
  '#ba68c8',
  '#ff9800',
  '#4caf50',
]

interface NestedGroupContextPros {
  level: number;
  updateChildRule: (prevRule: RuleDisplayTree, updRule: RuleDisplayTree) => void;
}

const NestedGroupContext = createContext<NestedGroupContextPros>({ level: 0 } as any)


const OperatorRule: FC<{id:string, rule:RuleDisplayTree}> = ({id: key, rule}) => {
  const { 
    setLeft, setRight, setOpKind, flipRule,
    states: { multipleValues, draftRule, isValid, errMsg, showRightExample, logicFlipped }, 
    leftRef,
    rightRef,
    operatorRef
  } = useOperatorRuleController(key, rule)
  const { states:{validationTouched} } = useContext(RuleComposeContext)

  const [uiStates, setUiStates] = useState({
    sideExplaination: { explainText: '' },
    globalDisabled: false,
  })

  useLmGlobalDisabledMsg({
    listener: (disabled) => {
      setUiStates(v => 
        v.globalDisabled !== disabled ? {...v, globalDisabled: disabled} : v
      )
    }
  })
  const rightProps = {
    disabled: uiStates.globalDisabled,
    ref: rightRef,
    rule: draftRule.Right!,
    onPickerChange: setRight,
    isValid: !validationTouched || isValid,
  }
  const {
    pickerAdjust: { adjustOpOptions },
    rightPickerOptions: { getPickerOptionProps },
    explaination: { generateRightExample, getOperatorOptionExplainationMap }
  } = useRuleDisplayPresetContext()
  const useOptionProps = getPickerOptionProps(draftRule)
  const rightExample = generateRightExample(draftRule)
  const {current: refBag} = useRef(({
    handleInputChange:(() => {}) as FormEventHandler<HTMLInputElement>,
    handleOptionClick: (() => {}) as MouseEventHandler<HTMLLIElement>,
    skipFilter: false,
    holdFocus: false,
    dropdownOpen: false,
    currOpOption: null as SimpleOption2|null,
    filteredOptions: opOptions,
  }))
  const filterOptions = ((options: SimpleOption2[], state: FilterOptionsState<SimpleOption2>) => {
    if (refBag.skipFilter) {
      refBag.filteredOptions = options
      return refBag.filteredOptions
    }
    const str = (op:SimpleOption2) => logicFlipped ? (op as any).flipText : op.text
    const trimmed = (state.inputValue ?? '').trim().toLowerCase()
    refBag.filteredOptions = options.filter(op => {
      return str(op).toLowerCase().indexOf(trimmed) > -1
    })
    return refBag.filteredOptions
  })
  const selectHighlighted = () => {
    const k = refBag.currOpOption ??
      opOptions.find(x => x.key === draftRule.Kind) ?? opOptions[0];
    const i = refBag.filteredOptions.indexOf(k);
    refBag.handleInputChange({ target: { value: !logicFlipped ? (k as any).flipText : k.text } } as any); // update the input with value of the highlighted option
    refBag.skipFilter = true; // prevent dropdown list from changing
    refBag.holdFocus = true; // make sure the dropdown list remain open after simulated clicking
    refBag.handleOptionClick({ target: {}, currentTarget: { getAttribute: () => i }, ctrlKey: true } as any); // simulate clicking/selecting currently highlighted option
    refBag.skipFilter = true; // prevent dropdown list from changing, again
  }
  const opOptionFlipBtnClick = (jumpNext?: boolean) => {
    selectHighlighted()
    flipRule()
    if (jumpNext) {
      setTimeout(() => { rightRef.current?.focus() }, 50)
    }
  }
  return (
    <Stack className="operator-placement" direction="row" alignItems="center" flex="1 1 auto" >
      <Box sx={{ mr: -1 }}>
        {draftRule.Left && <PickValueRule ref={leftRef} rule={draftRule.Left} onPickerChange={setLeft} disabled={uiStates.globalDisabled} />}
      </Box>
      <Box sx={{ 
        minWidth: logicFlipped ? 160 : 130,
        '& input': { color: 'info.main' },
        '& fieldset.MuiOutlinedInput-notchedOutline': { display: 'none' },
      }}>
        <SideExplainationContext.Provider value={uiStates.sideExplaination}>
          <FlipRuleContext.Provider value={{logicFlipped, flipRule: opOptionFlipBtnClick}}>
            <Autocomplete disableClearable={true} forcePopupIcon={false} PopperComponent={SideExplainationPopper140} disabled={uiStates.globalDisabled} 
              options={adjustOpOptions(draftRule) ?? opOptions}
              getOptionLabel={s => logicFlipped ? (s as any).flipText : s.text}
              renderInput={(params) => {
                refBag.handleInputChange = params.inputProps.onChange!
                return (<TextField {...params} inputRef={operatorRef} />  )
              }}
              value={opOptions.find(x => x.key === draftRule.Kind) ?? opOptions[0]}
              onChange={(e, value, reason) => {
                if (reason === 'selectOption') {
                  if (value) {
                    setOpKind(value.key as any, refBag.holdFocus)
                    refBag.holdFocus = false
                  }
                }
              }}
              onInputChange={(e, value, reason) => {
                if (reason === 'input') {
                  refBag.skipFilter = false
                }
              }}
              onOpen={() => refBag.dropdownOpen = true}
              onClose={() => {
                refBag.skipFilter = false
                refBag.holdFocus = false
                refBag.dropdownOpen = false
                setUiStates(v => ({...v, sideExplaination: { explainText: '' }})) 
              }}
              onHighlightChange={(e, option) => {
                const bag = getOperatorOptionExplainationMap(draftRule)
                refBag.currOpOption = option
                setUiStates(v => ({...v, sideExplaination: { explainText: option && (bag as any)[option.key] }}))
              }}
              filterOptions={filterOptions}
              onKeyDown={(event) => {
                // Wait until IME is settled.
                if (event.which !== 229) {
                  if (event.key === 'ArrowRight') {
                    selectHighlighted();
                    flipRule()
                  } else if (event.key === 'Enter') {
                    if (!refBag.dropdownOpen) {
                      setTimeout(() => rightRef.current?.focus(), 50)
                    }
                  }
                }
              }}
              renderOption={((props: React.HTMLAttributes<HTMLLIElement>, option: SimpleOption2, state: AutocompleteRenderOptionState) => {
                refBag.handleOptionClick = props.onClick!
                return (
                  <OpKindOption {...{flipRule: opOptionFlipBtnClick, props, option, state}} />
                )
              })}
            />
          </FlipRuleContext.Provider>
        </SideExplainationContext.Provider>
      </Box>
      <Box sx={{ minWidth: 200, flex: '1 1 auto', mr: 2 }}>
        {draftRule.Right && (
          useOptionProps ? <PickOptionSourceRule {...useOptionProps} {...rightProps} />
          : (multipleValues
            ? <PickConstantsRule {...rightProps} validator={draftRule.Kind === 'MatchesAny' ? validateSingleRegex : undefined} hasError={!!errMsg} />
            : <PickConstantRule  {...rightProps} />)
        )}
        {errMsg &&
          <FormHelperText error>{errMsg}</FormHelperText>}
        {!errMsg && showRightExample && rightExample &&
          <FormHelperText>{rightExample}</FormHelperText>}
      </Box>
    </Stack>
  )
}

const charClss = [
  'd', 'D', 'w', 'W', 's', 'S', 't', 'r', 'n', 'v', 'f', 'b', 'B', '0', 
  'c', 'x', 'u', 'p', 'P', 'k']
const specialChars = ['.', '\\', '^', '$', '|', '?', '*', '+', '(', ')', '[', '{']
const bsRgx = /\\+/g
const alphabetRgx = /^[a-zA-z]+/
const hexdecRgx = /^[0-9a-fA-F]+/
const countAfter = (s:string, i:number, rgx:RegExp) => {
  const m = s.substring(i).match(rgx)
  return m ? m[0].length : 0
}
const validateSingleRegex = (s:string) => {
  try {
    new RegExp(s) // try parse s as RegExp
    for (const m of s.matchAll(bsRgx)) {
      const t = m[0]
      if (t.length % 2 === 1) {
        const cursor = m.index + t.length
        const c = s.charAt(cursor)
        if (specialChars.indexOf(c) > -1) { continue }
        if (charClss.indexOf(c) === -1) { throw new Error() }
        else {
          if (c === 'c' || c === 'C') {
            if (countAfter(s, cursor + 1, alphabetRgx) < 1) { throw new Error() }
          } else if (c === 'x' || c === 'X') {
            if (countAfter(s, cursor + 1, hexdecRgx) < 2) { throw new Error() }
          } else if (c === 'u' || c === 'U') {
            if (
              countAfter(s, 
                s[cursor + 2] === '{' ? cursor + 2 : cursor + 1, 
                hexdecRgx) < 4
            ) { throw new Error() }
          } else if (c === 'p' || c === 'P') {
            if (s[cursor + 2] !== '{') { throw new Error() }
          }
        }
      } else {
        const cursor = m.index + t.length
        if (s.charAt(cursor) === '[' && s.charAt(cursor+1) === ']') { throw new Error() }
      }
    }
    let i = -2
    while ((i = s.indexOf('[]', i+2)) > -1) { // "[]" is not a valid C# Regex
      if (s.charAt(i - 1) !== '\\') { throw new Error() }
    }
  } catch {
    return 'Regular Expression value is incorrect'
  }
}


interface OperatorRuleContextProps {
  setLeft: (r: RuleDisplayTree) => void;
  setRight: (r: RuleDisplayTree) => void;
  setOpKind: (k: RuleDisplayKind, holdFocus?: boolean) => void;
  flipRule: () => void;
  states: {
    preRule: RuleDisplayTree;
    multipleValues: boolean;
    composedRule: RuleDisplayTree;
    draftRule: RuleDisplayTree;
    flippedRule: RuleDisplayTree;
    isValid: boolean;
    errMsg: string | undefined;
    showRightExample: boolean;
    logicFlipped: boolean;
  };
  leftRef: MutableRefObject<HTMLInputElement|undefined>;
  rightRef: MutableRefObject<HTMLInputElement|undefined>;
  operatorRef: MutableRefObject<HTMLInputElement|undefined>;
}


const opRuleStatesInit = (rule:RuleDisplayTree, defaultRule: () => RuleDisplayTree, defaultOpLeft: () => RuleDisplayTree["Left"], defaultOpRight: () => RuleDisplayTree["Right"]) => {
  const logicFlipped = rule.Kind === 'LogicNot'
  if (logicFlipped && !rule.Left) {
    rule.Left = defaultRule()
  }
  const actualRule = logicFlipped ? rule.Left! : rule
  const draftRule = {
    Kind: actualRule.Kind, 
    Left: actualRule.Left ?? defaultOpLeft(),
    Right: actualRule.Right ?? defaultOpRight(),
  } as RuleDisplayTree
  const flippedRule : RuleDisplayTree = {
    Kind: 'LogicNot',
    Left: draftRule,
    Right: { Kind: 'Noop' }
  }
  
  return ({
    preRule: rule,
    multipleValues: actualRule.Kind === 'EqualsAny' || actualRule.Kind === 'MatchesAny',
    composedRule: logicFlipped ? flippedRule : draftRule, 
    draftRule, // this field's obj ref should remain stable
    flippedRule,
    isValid: true,
    errMsg: undefined as string|undefined,
    showRightExample: false,
    logicFlipped,
  })
}
const useOperatorRuleController = (key:string, rule:RuleDisplayTree): OperatorRuleContextProps => {
  const { 
    defaults: {rule: defaultRule, opLeft: defaultOpLeft, opRight: defaultOpRight},
    pickerAdjust: {adjustOpKind}
  } = useRuleDisplayPresetContext()
  const [states, setStates] = useState(() => opRuleStatesInit(rule, defaultRule, defaultOpLeft, defaultOpRight))
  useEffect(() => setStates(opRuleStatesInit(rule, defaultRule, defaultOpLeft, defaultOpRight)), [rule, defaultOpLeft, defaultOpRight, defaultRule])
  const { updateChildRule: propagateUp } = useContext(NestedGroupContext)
  const leftRef = useRef<HTMLInputElement>()
  const rightRef = useRef<HTMLInputElement>()
  const operatorRef = useRef<HTMLInputElement>()

  const modifiers = useMemo(() => {
    const valueEqualsPrev = (v: typeof states) => {
      const preRuleLogicFlipped = v.preRule.Kind === 'LogicNot'
      const actualPreRule = preRuleLogicFlipped ? v.preRule.Left! : v.preRule
      if (preRuleLogicFlipped !== v.logicFlipped) { return false}
      if (actualPreRule.Kind !== v.draftRule.Kind) { return false }
      const leftEqual = (actualPreRule.Left === v.draftRule.Left ||
        (actualPreRule.Left?.Kind === v.draftRule.Left?.Kind
          && actualPreRule.Left?.Value === v.draftRule.Left?.Value
        )
      )
      if (!leftEqual) { return false }
      if (actualPreRule.Right === v.draftRule.Right) { return true }
      if (actualPreRule.Right?.Kind === v.draftRule.Right?.Kind) {
        if (actualPreRule.Kind === 'EqualsAny' || actualPreRule.Kind === 'MatchesAny') {
          const s0 = new Set(actualPreRule.Right?.Values ?? [])
          const s1 = new Set(v.draftRule.Right?.Values ?? [])
          const l1 = s1.size
          for (const x of s0) { s1.add(x) }
          return s0.size === s1.size && l1 === s1.size
        }
        return actualPreRule.Right?.Value === v.draftRule.Right?.Value
      }
      return false
    }
    const setLeft = (r:RuleDisplayTree) => {
      setStates(v => {
        if (v.draftRule.Left === r) { return v }
        v.draftRule.Left = r
        const errMsg = validatorSync(v.draftRule)
        v.errMsg = errMsg
        v.isValid = !errMsg
        setTimeout(() => 
          propagateUp(v.preRule, 
            valueEqualsPrev(v) ? v.preRule : v.composedRule))
        const opKindAdjust = adjustOpKind(r, v)
        if (opKindAdjust && operatorRules.has(opKindAdjust)) {
          setTimeout(() => setOpKind(opKindAdjust))
        }
        return {...v}

      })
      operatorRef.current?.focus()
    }
    const setRight = (r:RuleDisplayTree) => {
      setStates(v => {
        if (v.draftRule.Right === r) { return v }
        v.draftRule.Right = r
        const errMsg = validatorSync(v.draftRule)
        v.errMsg = errMsg
        v.isValid = !errMsg
        setTimeout(() => 
          propagateUp(v.preRule, 
            valueEqualsPrev(v) ? v.preRule : v.composedRule))
        return {...v}
      })
    }
    const setOpKind = (k: RuleDisplayKind, holdFocus?: boolean) => {
      if (!operatorRules.has(k)) return
      setStates(v => {
        if (v.draftRule.Kind === k) { return v }
        v.draftRule.Kind = k
        
        if (k === 'EqualsAny' || k === 'MatchesAny') {
          if (!v.multipleValues) {
            v.multipleValues = true
            v.draftRule.Right = { Kind: 'PickConstants', Values: [] }
          }
        } else {
          if (v.multipleValues) {
            v.multipleValues = false
            v.draftRule.Right = { Kind: 'PickConstant', Value: '' }
          }
        }
        const errMsg = validatorSync(v.draftRule)
        v.errMsg = errMsg
        v.isValid = !errMsg
        setTimeout(() => 
          propagateUp(v.preRule, 
            valueEqualsPrev(v) ? v.preRule : v.composedRule))
        return {...v}
      })
      if (!holdFocus) {
        setTimeout(() => rightRef.current?.focus(), 50)
      }
    }
    const flipRule = () => {
      if (operatorRef.current) {
        operatorRef.current.setSelectionRange(
          operatorRef.current.value.length,
          operatorRef.current.value.length,
        )
      }

      setStates(v => {
        const logicFlipped = !v.logicFlipped
        const composedRule = logicFlipped ? v.flippedRule : v.draftRule
        const v1 = {...v, logicFlipped, composedRule}
        setTimeout(() => 
          propagateUp(v1.preRule, 
            valueEqualsPrev(v1) ? v1.preRule : composedRule))
        return v1
      })
    }
    const validatorSync = (x: RuleDisplayTree):string|undefined => {
      const valid = Boolean(
        x.Left?.Value && 
        x.Right && (
            x.Right.Kind === 'PickConstant'  ? x.Right.Value
          : x.Right.Kind === 'PickConstants' ? (x.Right.Values && x.Right.Values.length > 0)
          : false
        ))
      if (!valid) { return 'Rule definition incomplete' }
      if (x.Kind === 'Matches') {
        return validateSingleRegex(x.Right!.Value as any)
      } else if (x.Kind === 'MatchesAny') {
        for (const n in x.Right!.Values!) {
          const err = validateSingleRegex(x.Right!.Values[n]! as any)
          if (err) { return err }
        }
      }
    }
    const validator = () => {
      return new Promise<boolean>(resolve => {
        let isValid = true
        setStates(v => {
          let shouldReload = false
          const errMsg = validatorSync(v.draftRule)
          if (v.errMsg !== errMsg) {
            v.errMsg = errMsg
            shouldReload = true
          }
          v.isValid = !errMsg
          isValid = v.isValid
          resolve(isValid)
          return shouldReload ? {...v} : v
        })
      })
    }
    return { setLeft, setRight, setOpKind, flipRule, validator }
  }, [adjustOpKind, propagateUp])

  useEffect(() => {
    if (rightRef.current) {
      let inputEl = rightRef.current
      const onFocus = () => { setStates(v => ({...v, showRightExample: true})) }
      const onBlur = () => { setStates(v => ({...v, showRightExample: false})) }
      inputEl.addEventListener('focus', onFocus)
      inputEl.addEventListener('blur', onBlur)
      return () => {
        inputEl.removeEventListener('focus', onFocus)
        inputEl.removeEventListener('blur', onBlur)
        inputEl = null as any
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rightRef.current])

  useLmStatesPropagate({
    key,
    asLowerStage: {
      collectStates: {
        handler() {
          return () => modifiers.validator().then(pass => pass ? Promise.resolve(pass) : Promise.reject(''))
        },
        deps: [modifiers.validator]
      }
    }
  })

  useLmRuleDisplayDeltaMsg({
    key,
    asLowerStage: {
      receiveDelta: {
        handler(_) {
          return (cmd) => {
            const d = cmd.delta
            const s0 = opRuleStatesInit(d, defaultRule, defaultOpLeft, defaultOpRight)
            s0.preRule = rule
            setStates(s0)
            setTimeout(() => 
              propagateUp(s0.preRule, s0.composedRule))
          }
        },
        deps: [rule]
      }
    }
  })

  return { states, ...modifiers, leftRef, rightRef, operatorRef }
}


interface PickerRuleProps {
  // ref: Ref<any>,
  rule:RuleDisplayTree, 
  onPickerChange:(x:RuleDisplayTree)=>void,
  disabled:boolean,
}


const PickValueRule = forwardRef<HTMLInputElement|undefined, PickerRuleProps>(({rule, onPickerChange, disabled}, ref) => {
  const [states, setStates] = useState({
    sideExplaination: { explainText: '' }
  })
  const { leftPickeValueOptions: pvOptions } = useRuleDisplayPresetContext()
  return (
    <Box sx={{ 
      minWidth: 120,
      '& input': { color: 'success.main', textAlign: 'right' },
      '& div.MuiInput-root.MuiInput-underline:before': { display: 'none' },
    }}>
      <SideExplainationContext.Provider value={states.sideExplaination}>
        <Autocomplete disableClearable={true} forcePopupIcon={false} PopperComponent={SideExplainationPopper130} disabled={disabled}
          options={pvOptions}
          getOptionLabel={s => s.text}
          renderInput={(params) =>
            <TextField {...params} variant="standard" inputRef={ref} sx={{ color: 'success.main' }} />
          }
          value={pvOptions.find(x => x.key === rule.Value) ?? pvOptions[0]}
          onChange={(e, value, reason) => {
            if (reason === 'selectOption') {
              if (value) {
                onPickerChange({...rule, Value: value.key})
              }
            }
          }}
          onClose={() => setStates(v => ({sideExplaination: { explainText: '' }})) }
          onHighlightChange={(e, option) => {
            setStates(v => ({sideExplaination: { explainText: option && (option as any).explain }}))
          }}
        />
      </SideExplainationContext.Provider>
    </Box>
  )
})


const SideExplainationContext = createContext({ explainText: '' })

const SideExplainationPopperBuilder = (widthFn: ()=>number):FC<any> => (props) => {
  const { children, ...rest } = props
  const { explainText } = (useContext as any)(SideExplainationContext)
  const { flipRule } = (useContext as any)(FlipRuleContext)
  const width = widthFn()
  return (
    <Popper {...rest} placement="bottom-start" style={{ width }} >
      <Box sx={{ position: 'relative',
        '& div.MuiPaper-root': { borderRadius: 'unset' }, 
        '& div.side-explain': {
          position: 'absolute',
          top: 0, left: '100%',
          width: 240, px: 2, py: 1,
          bgcolor: theme => theme.palette.background.default,
          opacity: 0.9,
        },
        '& li': {
          '&.Mui-focused svg': { color: theme => theme.palette.info.main },
          '& button': { width: 14, height: 14, mr: -1.5 },
          '& svg': { width: 14, height: 14 },
        },
      }}>
        {children}
        <Paper className="side-explain">
          {!!explainText &&
            <>
              <Typography variant="body2" color="info.light">{explainText}</Typography>
              <Divider sx={{ mt: 1, mb: 0.5}} />
            </>
          }
          <Box onMouseDown={(event) => {/* Prevent blur */ event.preventDefault(); }}>
            <Typography variant="caption" component="p" color="InactiveCaptionText" lineHeight={1.2} sx={{ transform: 'scale(0.8)', ml: -3 }}>
              <Typography variant="caption" component="p" sx={{m: 0}}>You can also:</Typography>
              <Link sx={{ cursor: 'pointer', mr: 0.5 }} color="info.light" onClick={(e) => {
                flipRule(true)
              }}>flip the rule</Link>{" "}(ArrowRight →)
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Popper>
  )
}

const SideExplainationPopper130 = SideExplainationPopperBuilder(() => 130)

const SideExplainationPopper140 = SideExplainationPopperBuilder(() => {
  const {logicFlipped} = useContext(FlipRuleContext)
  return logicFlipped ? 160 : 130
})

const PickConstantRule = forwardRef<HTMLInputElement|undefined, PickerRuleProps&{isValid:boolean}>(
  ({rule, onPickerChange, isValid, disabled}, ref) => {
  const timerRef = useRef(0)
  return (
    <Box sx={{ 
      '& input,textarea': { color: '#ad5324' },
    }}>
      <TextField inputRef={ref} fullWidth multiline error={!isValid} disabled={disabled}
        value={rule.Value ?? ''}
        onChange={(e) => {
          if (timerRef.current) {
            window.clearTimeout(timerRef.current)
            timerRef.current = 0
          }
          const value = e.target.value
          onPickerChange({...rule, Value: value})
          if (value.trim() !== value) {
            timerRef.current = window.setTimeout(() => onPickerChange({...rule, Value: value.trim()}), 1500)
          }
        }}
      />
    </Box>
  )
})

const PickConstantsRule = forwardRef<HTMLInputElement|undefined, PickerRuleProps&{isValid:boolean, validator?: (x:string)=>string|undefined, hasError: boolean}>(
  ({rule, onPickerChange, isValid, validator, disabled, hasError}, ref) => {
  const [states, setStates] = useState({ 
    inputValue: '',
    errmsg: null as string|null, 
  })
  const renderTags = (value: string[], getTagProps: AutocompleteRenderGetTagProps) => {
    return value.map((option, index) => (
      <Chip
        label={option ?? ''}
        {...getTagProps({ index })}
        {...(hasError && !!validator && !!validator(option) && {
          color: 'error', variant: "outlined"
        })}
      />
    ))
  }
  return (
    <Box sx={{ 
      '& div.MuiOutlinedInput-root': {
        '& > div.MuiChip-root': {
          height: 'auto', minHeight: 32,
          '& > span.MuiChip-label': {
            display: 'block',
            wordBreak: 'break-all',
            whiteSpace: 'break-spaces',
            color: '#ad5324',
          },
        },
        '& > input': { flex: '100%' },
      },
    }}>
      <Autocomplete multiple={true} freeSolo={true} disabled={disabled}
        options={[]} renderTags={renderTags}
        renderInput={(params) => 
          <TextField {...params} inputRef={ref} error={!isValid} placeholder="Add a new value and press the Enter key to confirm..." />
        } 
        inputValue={states.inputValue}
        value={(rule.Values ?? []) as string[]}
        onInputChange={(e, value) => setStates(v => {
          const v1 = {...v, inputValue: value}
          if (validator && v.errmsg) {
            v1.errmsg = null
          } 
          return v1
        })}
        onChange={(e, value, reason) => {
          if (reason === 'createOption') {
            const toAdd0 = ((value as string[])[value.length - 1] ?? '')
            const toAdd = toAdd0.trim()
            if (toAdd0 !== toAdd && value.indexOf(toAdd) > -1) {
              return
            }
            if (validator) { 
              const errmsg = validator(toAdd)
              if (errmsg) {
                setStates(v => ({...v, inputValue: toAdd, errmsg}))
                return
              } 
            }
            (value as string[])[value.length - 1] = toAdd;
            setStates(v => v.errmsg ? ({...v, errmsg:null}) : v)
          }
          onPickerChange({...rule, Values: value as any})
        }}
      />
      {validator && states.errmsg && <FormHelperText sx={{color:"warning.light"}} >{states.errmsg}</FormHelperText>}
    </Box>
  )
})


const PickerOptionAmbientDataContext = createContext<PickerOptionAmbientDataContextProps>({} as any)

interface PickerRuleProps {
  // ref: Ref<any>,
  rule:RuleDisplayTree, 
  onPickerChange:(x:RuleDisplayTree)=>void,
  disabled:boolean,
}

type RightPickerProps = PickerRuleProps & {
  isValid: boolean;
} & RefAttributes<HTMLInputElement | undefined>

export const PickOptionSourceRule : FC<{ optionSource: keyof PickerOptionAmbientDataContextProps, multiple: boolean}&RightPickerProps> = forwardRef(({optionSource, ...rest}, ref) => {
  const options = useContext(PickerOptionAmbientDataContext)[optionSource]
  const {rule, onPickerChange, isValid, multiple, disabled} = rest
  return (
    useMemo(() => <PickOption options={options} {...rest} ref={ref}/>, 
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options, rule, onPickerChange, isValid, multiple, disabled])
  )
})

const PickOption = forwardRef<HTMLInputElement|undefined, PickerRuleProps&{isValid:boolean, options: string[], multiple: boolean}>(
  ({rule, onPickerChange, isValid, options, multiple, disabled}, ref) => {

  return (
    <Box sx={{ 
      '& div.MuiOutlinedInput-root': {
        '& > div.MuiChip-root': {
          height: 'auto', minHeight: 32,
          '& > span.MuiChip-label': {
            display: 'block',
            wordBreak: 'break-all',
            whiteSpace: 'break-spaces',
            color: '#ad5324',
          },
        },
        '& > input': { flex: '100%' },
      },
    }}>
      <Autocomplete multiple={multiple} disabled={disabled}
        options={options}
        renderInput={(params) => 
          <TextField {...params} inputRef={ref} error={!isValid} />
        } 
        value={multiple ? (rule.Values ?? []) as string[]: (rule.Value as string)}
        onChange={(e, value, reason) => {
          onPickerChange({...rule, 
            ...(multiple ? {Values: value as any} : {Value: value as any})
          })
        }}
      />
    </Box>
  )
})

const FlipRuleContext = createContext({ logicFlipped: false, flipRule: (jumpNext?: boolean)=>{}})

const OpKindOption : FC<{props: React.HTMLAttributes<HTMLLIElement>, option: SimpleOption2, state: AutocompleteRenderOptionState}> = ({
  props, option, state,
}) => {
  const {logicFlipped} = useContext(FlipRuleContext)
  return (
    <li {...props}>
      <Stack direction="row" justifyContent="space-between" width="100%" alignItems="center">
        {logicFlipped ? (option as any).flipText : option.text} 
      </Stack>
    </li>
  )
}


const RULE_DELTA_MSG_TOPIC = 'OD_LM_RULE_DELTA_MSG_TOPIC'

interface UseLmRuleDisplayDeltaMsgProps {
  key: string,
  asLowerStage?: {
    receiveDelta: {
      handler: (sendDownDelta: (subkey: string, subDelta: RuleDisplayTree) => void) => (cmd: ReceiveDeltaCmdPayload) => void,
      deps: any[],
    }
  }
}
interface ReceiveDeltaCmdPayload {
  delta: RuleDisplayTree;
  targetKey: string;
}
interface RuleDisplayDeltaAction {
  sendDownDelta: (subkey: string, subDelta: RuleDisplayTree) => void;
}

export const useLmRuleDisplayDeltaMsg = (props: UseLmRuleDisplayDeltaMsgProps, subPub?: LocalMessageSubPub): RuleDisplayDeltaAction => {
  const ctx = useLocalMessagingContext()
  const subscribe = subPub?.subscribe ?? ctx.subscribe
  const publish = subPub?.publish ?? ctx.publish
  if (subscribe == null || publish == null) { throw 'Should use this hook inside a LocalMessagingContext' }
  
  const key = props.key
  const receiveDeltaHandlerFac = props.asLowerStage?.receiveDelta.handler ?? EmptyReceiveDeltaHandlerFac
  const receiveDeltaDeps = props.asLowerStage?.receiveDelta.deps ?? []

  const sendDownDelta = useCallback((subkey: string, subDelta: RuleDisplayTree) => {
    const payload: ReceiveDeltaCmdPayload = Object.freeze({ targetKey: subkey, delta: subDelta })
    publish({
      topic: RULE_DELTA_MSG_TOPIC,
      payload,
    })
  }, [publish])

  const receiveDelta = useMemo(() => {
    return {
      handler: receiveDeltaHandlerFac(sendDownDelta),
      skip: receiveDeltaHandlerFac === EmptyReceiveDeltaHandlerFac,
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, receiveDeltaDeps)

  useEffect(() => {
    return subscribe(
      receiveDelta.skip
      ? [Flt.withRelativeAddress.None]
      : [Flt.withTopic(RULE_DELTA_MSG_TOPIC), Flt.withRelativeAddress.Parent, 
         Flt.withCustom('target_me', (m) => (m.payload as ReceiveDeltaCmdPayload).targetKey === key)]
      , 
      (m) => {
        const cmd = m.payload as ReceiveDeltaCmdPayload
        receiveDelta.handler(cmd)
      })
  }, [subscribe, receiveDelta, key])

  return { sendDownDelta }
}

const EmptyReceiveDeltaHandlerFac = () => () => {}

const ruleEquals = (a: RuleDisplayTree, b: RuleDisplayTree): boolean => {
  if (a.Kind !== b.Kind) { return false }
  if (a.Kind === 'LogicNot') { return !!a.Left && !!b.Left && ruleEquals(a.Left, b.Left) }
  if (groupRules.has(a.Kind)) {
    if (!a.Group || !b.Group || a.Group?.length !== b.Group?.length) { return false }
    for (const i in a.Group) {
      if (!ruleEquals(a.Group[i], b.Group[i])) { return false }
    }
    return true
  }
  if (!a.Left || !b.Left || !a.Right || !b.Right) { return false }
  if (a.Left.Kind !== b.Left.Kind || a.Left.Value !== b.Left.Value) { return false }
  if (a.Right.Kind !== b.Right.Kind) { return false }
  if (a.Kind === 'EqualsAny' || a.Kind === 'MatchesAny') {
    if (!a.Right.Values || !b.Right.Values || a.Right.Values.length !== b.Right.Values.length) { return false }
    const s = new Set(a.Right.Values)
    return b.Right.Values.every(x => s.has(x))
  } else {
    return a.Right.Value === b.Right.Value
  }
}
